import { logger } from '@/lib/utils/logger';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

interface UseInfiniteScrollParams {
  queryKey: (string | Record<string, unknown>)[];
  queryFn: (params: Record<string, unknown>) => Promise<PageResult>;
  initialData?: PageResult;
  enabled?: boolean;
  filters?: Record<string, unknown>;
  limit?: number;
  scrollContainer?: Window | HTMLElement | null;
  viewMode?: string;
}

interface PageResult {
  totalPages: number;
  page: number;
  docs: Record<string, unknown>[];
  totalDocs?: number;
  hasNextPage?: boolean;
}

export function useInfiniteScroll({
  queryKey,
  queryFn,
  initialData,
  enabled = true,
  filters = {},
  limit = 8,
  scrollContainer,
  viewMode = '',
}: UseInfiniteScrollParams) {
  const { ...otherFilters } = filters;
  const isLoadingMore = useRef(false);
  const previousItemCount = useRef(0);
  const expectedNewItemCount = useRef(0);
  // logger.warn('isHorizontal', viewMode);


  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async function ({ pageParam }) {
      // Her sayfa için tüm filtreleri uygula
      const params = {
        page: pageParam,
        limit,
        ...otherFilters,
      };
      logger.debug(`[useInfiniteScroll] Sayfa ${pageParam} için veri yükleniyor, limit: ${limit}`);
      const result = await queryFn(params);
      logger.debug(`[useInfiniteScroll] Sayfa ${pageParam} için veri yüklendi:`, {
        totalDocs: result?.totalDocs,
        totalPages: result?.totalPages,
        hasNextPage: result?.hasNextPage,
        docsLength: result?.docs?.length,
      });

      // Yeni sayfa yüklendiğinde, beklenen yeni öğe sayısını kaydet
      if (result?.docs) {
        expectedNewItemCount.current = result.docs.length;
      }

      return result;
    },
    getNextPageParam: function (lastPage) {
      // Eğer toplam sayfa sayısı mevcut sayfadan büyükse, bir sonraki sayfayı döndür
      if (lastPage.totalPages > lastPage.page) {
        logger.debug(`[useInfiniteScroll] Sonraki sayfa var: ${lastPage.page + 1} / ${lastPage.totalPages}`);
        return lastPage.page + 1;
      }
      logger.debug(
        `[useInfiniteScroll] Sonraki sayfa yok. Mevcut sayfa: ${lastPage.page}, Toplam sayfa: ${lastPage.totalPages}`
      );
      return undefined;
    },
    initialData: initialData
      ? {
        pages: [initialData],
        pageParams: [1],
      }
      : undefined,
    enabled,
    gcTime: 30 * 60 * 1000, // 30 dakika
    staleTime: 5 * 60 * 1000, // 5 dakika
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Toplam öğe sayısını izle
  useEffect(
    function () {
      const currentItemCount = query.data?.pages.flatMap(page => page.docs || []).length || 0;

      if (currentItemCount > previousItemCount.current) {
        const addedItemCount = currentItemCount - previousItemCount.current;

        logger.debug(`[useInfiniteScroll] Yeni öğeler eklendi:`, {
          öncekiSayı: previousItemCount.current,
          şimdikiSayı: currentItemCount,
          eklenenSayı: addedItemCount,
          beklenenSayı: expectedNewItemCount.current,
        });

        // Yükleme tamamlandığında isLoadingMore'u sıfırla
        if (!query.isFetchingNextPage) {
          isLoadingMore.current = false;
        }

        // Öğe sayısını güncelle
        previousItemCount.current = currentItemCount;
      }
    },
    [query.data?.pages, query.isFetchingNextPage]
  );

  // Client-side only code
  useEffect(
    function () {

      logger.debug('isHorizontal', viewMode === 'gallery');
      logger.debug(`[useInfiniteScroll] Scroll olayı tetiklendi`);
      logger.debug('scrollContainer', scrollContainer);
      // Eğer scrollContainer tanımlı değilse, işlemi atla
      if (!scrollContainer) return undefined;

      // Yeni sayfa yükleme fonksiyonu
      const loadNextPage: () => Promise<void> = async () => {
        logger.debug(`[useInfiniteScroll] Yeni sayfa yükleniyor...`);

        isLoadingMore.current = true;

        try {
          await query.fetchNextPage();
          logger.debug(`[useInfiniteScroll] Yeni sayfa yüklendi`);
        } catch (error) {
          logger.error(`[useInfiniteScroll] Sayfa yüklenirken hata:`, error);
        } finally {
          isLoadingMore.current = false;
        }
      };

      // Scroll işleyici fonksiyonu
      function handleScroll() {
        logger.debug(`[useInfiniteScroll] handleScroll çalıştı`);

        // Eğer zaten yükleme yapılıyorsa veya daha fazla sayfa yoksa, işlemi atla
        if (!enabled || isLoadingMore.current || !query.hasNextPage) {
          return;
        }

        // Scroll pozisyonunu ve eşik değerini hesapla
        let scrollPosition = 0;
        let scrollThreshold = 0;

        // Yatay scroll için
        if (viewMode === 'gallery') {
          // window nesnesi için
          if (scrollContainer === window) {
            scrollPosition = window.innerWidth + window.scrollX;
            scrollThreshold = document.documentElement.scrollWidth - 500;
          }
          // DOM elementi için
          else if (scrollContainer instanceof HTMLElement) {
            scrollPosition = scrollContainer.clientWidth + scrollContainer.scrollLeft;
            scrollThreshold = scrollContainer.scrollWidth - 500;
          }
        }
        // Dikey scroll için (mevcut kod)
        else {
          // window nesnesi için
          if (scrollContainer === window) {
            scrollPosition = window.innerHeight + window.scrollY;
            scrollThreshold = document.documentElement.scrollHeight - 500;
          }
          // DOM elementi için
          else if (scrollContainer instanceof HTMLElement) {
            scrollPosition = scrollContainer.clientHeight + scrollContainer.scrollTop;
            scrollThreshold = scrollContainer.scrollHeight - 500;
          }
        }

        // Scroll eşiğine ulaşıldığında yeni sayfa yükle
        if (scrollPosition >= scrollThreshold) {
          loadNextPage();
        }
      }

      // İçerik boyutunu kontrol et ve gerekirse yeni sayfa yükle
      function checkContentSize() {
        logger.debug(`[useInfiniteScroll] checkContentSize çalıştı`);

        if (!enabled || isLoadingMore.current || !query.hasNextPage) {
          return;
        }

        let contentHeight = 0;
        let viewportHeight = 0;

        if (viewMode === 'gallery') {
          // Yatay scroll için
          if (scrollContainer === window) {
            contentHeight = document.documentElement.scrollWidth;
            viewportHeight = window.innerWidth;
          } else if (scrollContainer instanceof HTMLElement) {
            contentHeight = scrollContainer.scrollWidth;
            viewportHeight = scrollContainer.clientWidth;
            logger.debug(
              `[useInfiniteScroll] Yatay scroll için contentHeight: ${contentHeight}, viewportHeight: ${viewportHeight}`
            );
          }
        } else {
          // Dikey scroll için
          if (scrollContainer === window) {
            contentHeight = document.documentElement.scrollHeight;
            viewportHeight = window.innerHeight;
          } else if (scrollContainer instanceof HTMLElement) {
            contentHeight = scrollContainer.scrollHeight;
            viewportHeight = scrollContainer.clientHeight;
            logger.debug(
              `[useInfiniteScroll] Dikey scroll için contentHeight: ${contentHeight}, viewportHeight: ${viewportHeight}`
            );
          }
        }

        if (contentHeight === 0 || viewportHeight === 0) return;

        // İçerik viewport'tan küçükse ve daha fazla sayfa varsa, yeni sayfa yükle
        if (contentHeight <= viewportHeight && query.hasNextPage) {
          loadNextPage();
        }
      }

      // ResizeObserver oluştur
      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          // ResizeObserver tetiklendiğinde içerik boyutunu kontrol et
          logger.debug(`[useInfiniteScroll] ResizeObserver tetiklendi`);
          checkContentSize();
        });

        // Scroll container'ı izlemeye başla
        if (scrollContainer instanceof HTMLElement) {
          resizeObserver.observe(scrollContainer);
        } else if (scrollContainer === window) {
          // Window için document.body'yi izle
          resizeObserver.observe(document.body);
        }
      }
      // Veri değiştiğinde de içerik boyutunu kontrol et
      if (query.data && !query.isFetching) {
        setTimeout(checkContentSize, 100);
      }

      // Scroll olayını dinle
      if (scrollContainer === window) {
        logger.debug(`[useInfiniteScroll] Window scroll olayı dinleniyor`);
        window.addEventListener('scroll', handleScroll);
      } else if (scrollContainer instanceof HTMLElement) {
        logger.debug(`[useInfiniteScroll] Element scroll olayı dinleniyor`);
        scrollContainer.addEventListener('scroll', handleScroll);
      }

      // Cleanup
      return function () {
        if (scrollContainer === window) {
          window.removeEventListener('scroll', handleScroll);
        } else if (scrollContainer instanceof HTMLElement) {
          scrollContainer.removeEventListener('scroll', handleScroll);
        }
        // ResizeObserver'ı temizle
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    },
    [query.hasNextPage, query.isFetchingNextPage, enabled, query.fetchNextPage, query, scrollContainer, viewMode]
  );

  return query;
}

// TypeScript için global window tanımını genişlet
declare global {
  interface Window {
    scrollThrottleTimeout?: NodeJS.Timeout;
  }

  interface HTMLElement {
    scrollThrottleTimeout?: NodeJS.Timeout;
  }
}
